import { request } from './core'
import type {
  User,
  UserActivePicker,
  UserRequest,
  AdminUsersResponse,
  AdminUsersPaginatedResponse,
  AdminUsersPickerPaginatedResponse,
  AdminRolesPaginatedResponse,
  Role,
  RoleCreate,
  RoleTemplate,
  PublicTenant,
} from './types'

export interface EntityTransferItem {
  entity_type: string
  entity_ids: string[]
  target_user_id: string
}

export interface OwnedEntitiesResponse {
  user_id: string
  tenant_id: string
  entity_counts: Record<string, number>
  entities: Record<string, Array<{ id: string; name: string; project_name?: string }>>
  has_entities: boolean
  total_count: number
}

export interface ReassignTenantPayload {
  target_tenant_id: string
  target_role_ids: string[]
  entity_transfers?: EntityTransferItem[]
  force?: boolean
}

export type AdminUserStatus = 'pending' | 'active' | 'rejected' | 'disabled'

/** Legacy grouped list (omit status) vs single-status server-paginated list */
export type ListUsersParams =
  | { search?: string; role_id?: string }
  | { status: AdminUserStatus; search?: string; page?: number; page_size?: number; role_id?: string }

function buildAdminUsersQuery(params?: ListUsersParams): string {
  const q = new URLSearchParams()
  if (params && 'status' in params && params.status) {
    q.set('status', params.status)
    if (params.search) q.set('search', params.search)
    if (params.role_id) q.set('role_id', params.role_id)
    q.set('page', String(params.page ?? 1))
    q.set('page_size', String(params.page_size ?? 10))
  } else {
    const legacy = params as { search?: string; role_id?: string } | undefined
    if (legacy?.search) q.set('search', legacy.search)
    if (legacy?.role_id) q.set('role_id', legacy.role_id)
  }
  const s = q.toString()
  return s ? `?${s}` : ''
}

function buildAdminRolesQuery(params?: {
  search?: string
  page?: number
  page_size?: number
}): string {
  const q = new URLSearchParams()
  q.set('page', String(params?.page ?? 1))
  q.set('page_size', String(params?.page_size ?? 10))
  if (params?.search) q.set('search', params.search)
  return `?${q.toString()}`
}

export const adminApi = {
  /**
   * List users: without `status`, returns legacy grouped buckets; with `status`, returns full list.
   */
  listUsers: (
    params?: ListUsersParams,
    options?: { signal?: AbortSignal }
  ): Promise<AdminUsersResponse | AdminUsersPaginatedResponse> =>
    request<AdminUsersResponse | AdminUsersPaginatedResponse>(
      `/admin/users${buildAdminUsersQuery(params)}`,
      { signal: options?.signal }
    ),

  /**
   * Active + pending users for pickers (modals). Does not use the grouped endpoint.
   */
  listUsersForPicker: async (
    search?: string,
    options?: { signal?: AbortSignal }
  ): Promise<User[]> => {
    const signal = options?.signal
    const searchQ =
      search && search.trim() ? `&search=${encodeURIComponent(search.trim())}` : ''
    const [activeRes, pendingRes] = await Promise.all([
      request<AdminUsersPaginatedResponse>(
        `/admin/users?status=active&page=1&page_size=100${searchQ}`,
        { signal }
      ),
      request<AdminUsersPaginatedResponse>(
        `/admin/users?status=pending&page=1&page_size=100${searchQ}`,
        { signal }
      ),
    ])
    const byId = new Map<string, User>()
    for (const u of activeRes.items) byId.set(u.id, u)
    for (const u of pendingRes.items) byId.set(u.id, u)
    return Array.from(byId.values()).sort((a, b) =>
      (a.display_name || a.email).localeCompare(b.display_name || b.email, undefined, {
        sensitivity: 'base',
      })
    )
  },

  /**
   * Active users for selects/filters.
   * Defaults to `fields=picker` (slim id/email/display_name/status/tenant_id).
   * Pass `fields: 'full'` for legacy UserRead rows. With `page` and/or `page_size`:
   * paginated `{ items, total, ... }`.
   */
    listActiveUsers: (
    params?: {
      search?: string
      page?: number
      page_size?: number
      fields?: 'picker' | 'full'
      include_platform_admins?: boolean
    },
    options?: { signal?: AbortSignal }
  ): Promise<
    | UserActivePicker[]
    | User[]
    | AdminUsersPickerPaginatedResponse
    | AdminUsersPaginatedResponse
  > => {
    const q = new URLSearchParams()
    const fields = params?.fields ?? 'picker'
    q.set('fields', fields)
    if (params?.search?.trim()) q.set('search', params.search.trim())
    if (params?.page != null) q.set('page', String(params.page))
    if (params?.page_size != null) q.set('page_size', String(params.page_size))
    if (params?.include_platform_admins) q.set('include_platform_admins', 'true')
    const qs = q.toString()
    const querySuffix = qs ? `?${qs}` : ''
    return request<
      | UserActivePicker[]
      | User[]
      | AdminUsersPickerPaginatedResponse
      | AdminUsersPaginatedResponse
    >(`/admin/users/active${querySuffix}`, { signal: options?.signal })
  },

  /** List tenants available for registration (public endpoint, no auth required) */
  listPublicTenants: () => request<PublicTenant[]>('/admin/tenants/public'),

  requestAccess: (data: UserRequest) =>
    request<User>('/admin/users/request', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  approveUser: (userId: string, roleIds: string[], tenantId?: string) =>
    request<User>(`/admin/users/${userId}/approve`, {
      method: 'POST',
      body: JSON.stringify({
        role_ids: roleIds,
        ...(tenantId && { tenant_id: tenantId }),
      }),
    }),

  rejectUser: (userId: string) =>
    request<void>(`/admin/users/${userId}/reject`, {
      method: 'POST',
    }),

  bulkApproveUsers: (userIds: string[], roleIds: string[], tenantId?: string) =>
    request<{ successful_ids: string[]; failed_ids: string[]; errors: string[] }>(
      '/admin/users/bulk-approve',
      {
        method: 'POST',
        body: JSON.stringify({
          user_ids: userIds,
          role_ids: roleIds,
          ...(tenantId && { tenant_id: tenantId }),
        }),
      }
    ),

  bulkRejectUsers: (userIds: string[]) =>
    request<{ successful_ids: string[]; failed_ids: string[]; errors: string[] }>(
      '/admin/users/bulk-reject',
      {
        method: 'POST',
        body: JSON.stringify({
          user_ids: userIds,
        }),
      }
    ),

  /** Move a rejected user back to pending (server enforces tenant scope for non–platform-admins). */
  requeueRejectedUser: (userId: string) =>
    request<User>(`/admin/users/${userId}/requeue-rejected`, {
      method: 'POST',
    }),

  updateUser: (userId: string, data: Partial<UserRequest>) =>
    request<User>(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteUser: (userId: string) =>
    request<void>(`/admin/users/${userId}`, {
      method: 'DELETE',
    }),

  enableUser: (userId: string) =>
    request<{ message: string; user_id: string; status: string }>(
      `/admin/users/${userId}/enable`,
      { method: 'POST' }
    ),

  bulkRemoveUsers: (userIds: string[]) =>
    request<{ deleted: number; skipped: number; errors: string[] }>('/admin/users/bulk-remove', {
      method: 'POST',
      body: JSON.stringify({ user_ids: userIds }),
    }),

  resetUserPassword: async (userId: string) => {
    const reset = await request<{
      message: string
      user_id: string
      user_email: string
      temporary_password_encrypted: string
      email_sent: boolean
      password_change_required: boolean
      note: string
    }>(`/admin/users/${userId}/reset-password`, {
      method: 'POST',
    })

    const decrypted = await request<{ temporary_password: string }>(
      '/admin/users/decrypt-temporary-password',
      {
        method: 'POST',
        body: JSON.stringify({
          temporary_password_encrypted: reset.temporary_password_encrypted,
        }),
      }
    )

    return {
      ...reset,
      temporary_password: decrypted.temporary_password,
    }
  },

  listRoles: (
    params?: { search?: string; page?: number; page_size?: number },
    options?: { signal?: AbortSignal }
  ) =>
    request<AdminRolesPaginatedResponse>(`/admin/roles${buildAdminRolesQuery(params)}`, {
      signal: options?.signal,
    }),

  /**
   * List all roles for dropdown/select components (unpaginated).
   * For platform admins, can filter by specific tenant_id.
   */
  listRolesUnpaginated: (tenantId?: string, options?: { signal?: AbortSignal }) => {
    const query = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : ''
    return request<Role[]>(`/admin/roles/unpaginated${query}`, { signal: options?.signal })
  },

  /**
   * Get available role templates for creating new roles.
   * These templates are pre-configured role definitions (Admin, Developer, etc.)
   * that can be used to quickly create standard roles for a tenant.
   */
  listRoleTemplates: (options?: { signal?: AbortSignal }) =>
    request<RoleTemplate[]>('/admin/roles/templates', { signal: options?.signal }),

  getRole: (roleId: string, options?: { signal?: AbortSignal }) =>
    request<Role>(`/admin/roles/${roleId}`, { signal: options?.signal }),

  createRole: (data: RoleCreate) =>
    request<Role>('/admin/roles', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateRole: (roleId: string, data: RoleCreate) =>
    request<Role>(`/admin/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteRole: (roleId: string) =>
    request<void>(`/admin/roles/${roleId}`, {
      method: 'DELETE',
    }),

  /**
   * Check dependencies for selected roles (users assigned to those roles).
   * Used before single or bulk delete to show dependency modal when blocked.
   */
  checkRoleDependencies: (roleIds: string[]) =>
    request<import('../../components/DependencyWarningModal').DependencyCheckResult>(
      '/admin/roles/check-dependencies',
      {
        method: 'POST',
        body: JSON.stringify({ role_ids: roleIds }),
      }
    ),

  /**
   * Delete multiple roles. Fails with 409 if any role has users assigned.
   */
  bulkDeleteRoles: (roleIds: string[]) =>
    request<{ deleted: number; skipped: number; errors: string[] }>('/admin/roles/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ role_ids: roleIds }),
    }),

  /** Grant Platform Admin role to a user (requires Platform Admin) */
  grantPlatformAdmin: (userId: string) =>
    request<{ message: string; user_id: string; is_platform_admin: boolean; total_platform_admins: number }>(
      `/admin/users/${userId}/grant-platform-admin`,
      { method: 'POST' }
    ),

  /** Revoke Platform Admin role from a user (requires Platform Admin, fails if last admin) */
  revokePlatformAdmin: (userId: string) =>
    request<{ message: string; user_id: string; is_platform_admin: boolean; total_platform_admins: number }>(
      `/admin/users/${userId}/revoke-platform-admin`,
      { method: 'POST' }
    ),

  getOwnedEntities: (userId: string, options?: { signal?: AbortSignal }) =>
    request<OwnedEntitiesResponse>(`/admin/users/${userId}/owned-entities`, { signal: options?.signal }),

  transferEntities: (userId: string, data: { transfers: EntityTransferItem[] }) =>
    request<{ message: string; transferred: Record<string, number> }>(`/admin/users/${userId}/transfer-entities`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  reassignTenant: (userId: string, data: ReassignTenantPayload) =>
    request<User>(`/admin/users/${userId}/reassign-tenant`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  listExternalAgentDiscoveryGaps: (params?: {
    page?: number
    page_size?: number
    search?: string
  }) => {
    const searchParams = new URLSearchParams()
    if (params?.page !== undefined) searchParams.set('page', String(params.page))
    if (params?.page_size !== undefined) searchParams.set('page_size', String(params.page_size))
    const searchTerm = params?.search?.trim()
    if (searchTerm) searchParams.set('search', searchTerm)
    const query = searchParams.toString()
    return request<ExternalAgentDiscoveryGapList>(
      `/admin/external-agents/discovery-gaps${query ? `?${query}` : ''}`,
    )
  },
}

export interface ExternalAgentDiscoveryGapItem {
  id: string
  name: string
  platform: string
  project_id: string
  project_name?: string | null
  endpoint_host?: string
  created_by?: string | null
  creator_name?: string | null
  created_at?: number | null
}

export interface ExternalAgentDiscoveryGapList {
  items: ExternalAgentDiscoveryGapItem[]
  total: number
  page: number
  page_size: number
}
